import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaMinus, FaQuestionCircle } from 'react-icons/fa';
import { apiService } from '../services/api';
import SEO from '../components/SEO';

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    apiService.faq.getAll()
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setFaqs(data);

        const uniqueCategories = ['All', ...new Set(data.map((faq) => (faq && faq.category) || 'General'))];
        setCategories(uniqueCategories);
        setLoading(false);
      })
      .catch(() => {
        // Mock data fallback
        setFaqs([
          {
            id: 1,
            question: 'What is the admission process?',
            answer: 'Admission forms are available at school office. Submit with required documents.',
            category: 'Admission'
          },
          {
            id: 2,
            question: 'What is the fee structure?',
            answer: 'Fee structure varies by class. Contact school office for detailed information.',
            category: 'Fees'
          },
          {
            id: 3,
            question: 'Do you provide transportation?',
            answer: 'Yes, we have bus facility covering major areas of Panipat and surrounding regions.',
            category: 'Facilities'
          }
        ]);
        setCategories(['All', 'Admission', 'Fees', 'Facilities']);
        setLoading(false);
      });
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const filteredFaqs = selectedCategory === 'All'
    ? faqs
    : faqs.filter((faq) => (faq.category || 'General') === selectedCategory);

  return (
    <>
      <SEO 
        title="Frequently Asked Questions | Vidya Vatika World School Panipat"
        description="Find answers to common questions about admissions, academics, fees, facilities and more at Vidya Vatika World School. Quick help center."
        keywords="school FAQ Panipat, admission queries, common questions CBSE, school information Haryana, help center, FAQs about school"
        canonicalUrl="/faq"
        ogImage="/images/og-image.jpg"
      />
      
      <div className="pt-20 min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-purple-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Find answers to common questions about Vidya Vatika World School
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-900 text-white shadow-lg'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {loading ? (
            <div className="text-center py-12">
              <div className="spinner w-16 h-16 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading FAQs...</p>
            </div>
          ) : filteredFaqs.length === 0 ? (
            <p className="text-center text-gray-600 py-12">No FAQs in this category yet. Please check back soon.</p>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-5 flex items-center justify-between hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <FaQuestionCircle className="text-2xl text-blue-900" />
                      <span className="text-lg font-semibold text-gray-800 text-left">
                        {faq.question}
                      </span>
                    </div>
                    <motion.div
                      animate={{ rotate: openIndex === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-blue-900"
                    >
                      {openIndex === index ? <FaMinus /> : <FaPlus />}
                    </motion.div>
                  </button>
                  
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 pt-2 text-gray-700 leading-relaxed border-t">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}

          {filteredFaqs.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No FAQs found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-purple-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">
            Still Have Questions?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            We're here to help! Contact us for any queries.
          </p>
          <a
            href="/contact"
            className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-semibold transition-all text-lg shadow-lg hover:shadow-xl"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
    </>
  );
};

export default FAQPage;
